// ShiftGlobalSearchCommandPalette.tsx
import React, { FC, useEffect, useMemo, useRef, useState } from "react";

import { useTime } from "@/app/application/TimeContext";
import { formatShiftDateTime, parseShiftDateTime } from "@/features/shift/lib";
import { ShiftEventResponse } from "@/features/shift/model/Shift/Shift";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import {
	SearchPaletteAttributeList,
	SearchPaletteQuickLookPanel,
	SearchPaletteSectionHeader,
} from "@/shared/ui/search-palette/SearchPaletteBlocks";
import {
	searchPaletteAccentChipSx,
	searchPaletteInputSx,
	searchPaletteMetaTextSx,
	searchPaletteNeutralChipSx,
	searchPalettePaperSx,
	searchPaletteQuickLookToggleSx,
	searchPaletteRowSx,
	searchPaletteSummaryTextSx,
} from "@/shared/ui/search-palette/searchPaletteStyles";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import LaunchIcon from "@mui/icons-material/Launch";
import PreviewIcon from "@mui/icons-material/Preview";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	Divider,
	IconButton,
	InputAdornment,
	List,
	ListItemButton,
	Stack,
	TextField,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";

import { useFetchShift } from "@/service/ShiftHomeQueries";

type Props = {
	open: boolean;
	onClose: () => void;

	/** Open the ShiftEvent modal (recommended) */
	onOpenItem: (row: ShiftEventResponse) => void;

	/** Optional: enable Ctrl/Cmd+Enter new tab if you can map a route */
	buildItemUrl?: (row: ShiftEventResponse) => string | null;

	placeholder?: string;

	/** keep parity with procedures palette */
	enablePreview?: boolean;
};

const MIN_LEN = 2;
const PAGE_SIZE = 25;
type ShiftFilterKey = "shiftLog" | "handover" | "outOfService";
type ShiftSearchRow = ShiftEventResponse;

const SHIFT_FILTER_OPTIONS: Array<{
	key: ShiftFilterKey;
	label: string;
}> = [
	{ key: "shiftLog", label: "Shift log" },
	{ key: "handover", label: "Handover" },
	{ key: "outOfService", label: "Out of service" },
];

function useDebounced(value: string, delay = 250): string {
	return useDebouncedValue(value, delay);
}

function stripHtml(s?: string) {
	return (s || "")
		.replace(/<[^>]*>/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

function pickString(...values: unknown[]) {
	for (const value of values) {
		if (typeof value !== "string") continue;
		const trimmed = value.trim();
		if (trimmed) return trimmed;
	}
	return "";
}

function getRowDescription(row: ShiftSearchRow) {
	return pickString(row.Description, row.description);
}

function getRowLoggedBy(row: ShiftSearchRow) {
	return pickString(row.LoggedBy, row.loggedBy);
}

function getRowDateValue(row: ShiftSearchRow): unknown {
	return row.Date ?? row.date;
}

function getRowGroups(row: ShiftSearchRow) {
	return row.Groups ?? row.groups ?? [];
}

function getRowSystems(row: ShiftSearchRow) {
	return row.Systems ?? row.systems ?? [];
}

function getRowCrew(row: ShiftSearchRow) {
	return row.ShiftCrew ?? row.shiftCrew;
}

function isTruthyFlag(value: unknown) {
	return value === true || value === 1 || value === "1";
}

function isHandoverRow(row: ShiftSearchRow) {
	return isTruthyFlag(row.IsHandoverEvent ?? row.isHandoverEvent);
}

function isOutOfServiceRow(row: ShiftSearchRow) {
	return (
		isTruthyFlag(row.IsOutOfServiceEvent ?? row.isOutOfServiceEvent) ||
		isTruthyFlag(row.IsOutOfService ?? row.isOutOfService)
	);
}

function getShiftCategory(row: ShiftSearchRow): ShiftFilterKey {
	if (isHandoverRow(row)) return "handover";
	if (isOutOfServiceRow(row)) return "outOfService";
	return "shiftLog";
}

function getShiftFilterLabel(filter: ShiftFilterKey) {
	return (
		SHIFT_FILTER_OPTIONS.find((option) => option.key === filter)?.label ?? filter
	);
}

function getShiftFilterAccent(theme: Theme, filter: ShiftFilterKey) {
	switch (filter) {
		case "handover":
			return theme.palette.warning.main;
		case "outOfService":
			return theme.palette.error.main;
		case "shiftLog":
		default:
			return theme.palette.primary.main;
	}
}

function getShiftFilterTint(
	theme: Theme,
	filter: ShiftFilterKey,
	opacity: number,
) {
	return alpha(getShiftFilterAccent(theme, filter), opacity);
}

export const ShiftGlobalSearchCommandPalette: FC<Props> = ({
	open,
	onClose,
	onOpenItem,
	buildItemUrl,
	placeholder = "Search shift events…",
	enablePreview = true,
}) => {
	const theme = useTheme();
	const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
	const { timezone } = useTime();

	const [query, setQuery] = useState("");
	const debounced = useDebounced(query, 250);
	const canSearch = debounced.trim().length >= MIN_LEN;

	const inputRef = useRef<HTMLInputElement | null>(null);
	const listRef = useRef<HTMLUListElement | null>(null);

	const [activeIndex, setActiveIndex] = useState(0);
	const [quickLookOpen, setQuickLookOpen] = useState(false);
	const [filters, setFilters] = useState<Set<ShiftFilterKey>>(new Set());

	const { data, isLoading: isFetching } = useFetchShift(
		{
			GroupId: undefined, // always all groups
			IsFromShiftStart: true,
			Page: 1,
			PageSize: PAGE_SIZE,
			Search: canSearch ? debounced.trim() : undefined,
		},
		{
			enabled: open && canSearch,
		},
	);

	const items = useMemo(() => {
		if (!canSearch) return [];
		const arr = data?.Items ?? data?.items ?? [];
		return arr.slice().sort((a, b) => {
			const aTime =
				parseShiftDateTime(a.Date ?? a.date, timezone)?.valueOf() ??
				parseShiftDateTime(a.Created ?? a.created, timezone)?.valueOf() ??
				0;
			const bTime =
				parseShiftDateTime(b.Date ?? b.date, timezone)?.valueOf() ??
				parseShiftDateTime(b.Created ?? b.created, timezone)?.valueOf() ??
				0;
			return bTime - aTime;
		});
	}, [canSearch, data, timezone]);

	const toggleFilter = (filter: ShiftFilterKey) => {
		setFilters((prev) => {
			const next = new Set(prev);
			if (next.has(filter)) next.delete(filter);
			else next.add(filter);
			return next;
		});
	};

	const clearFilters = () => setFilters(new Set());

	const filteredItems = useMemo(() => {
		if (filters.size === 0) return items;
		return items.filter((row) => {
			if (filters.has("handover") && isHandoverRow(row)) return true;
			if (filters.has("outOfService") && isOutOfServiceRow(row)) return true;
			if (
				filters.has("shiftLog") &&
				!isHandoverRow(row) &&
				!isOutOfServiceRow(row)
			) {
				return true;
			}
			return false;
		});
	}, [filters, items]);

	const { groups, flat } = useMemo(() => {
		const flatItems: ShiftSearchRow[] = [];
		const grouped = SHIFT_FILTER_OPTIONS.map((option) => {
			const categoryItems = filteredItems.filter(
				(row) => getShiftCategory(row) === option.key,
			);
			if (!categoryItems.length) return null;

			const indexedItems = categoryItems.map((item) => {
				const index = flatItems.length;
				flatItems.push(item);
				return { item, index };
			});

			return {
				filter: option.key,
				label: option.label,
				items: indexedItems,
			};
		}).filter(
			(
				group,
			): group is {
				filter: ShiftFilterKey;
				label: string;
				items: Array<{ item: ShiftSearchRow; index: number }>;
			} => Boolean(group),
		);

		return { groups: grouped, flat: flatItems };
	}, [filteredItems]);

	const activeItem = flat[activeIndex] ?? null;

	useEffect(() => {
		if (!open) return;
		setQuery("");
		setActiveIndex(0);
		setQuickLookOpen(false);
		setFilters(new Set());
		setTimeout(() => inputRef.current?.focus(), 30);
	}, [open]);

	useEffect(() => {
		if (!open) return;
		setActiveIndex(0);
		setQuickLookOpen(false);
	}, [debounced, filters, open]);

	// keep selected visible
	useEffect(() => {
		if (!open) return;
		const ul = listRef.current;
		if (!ul) return;
		const el = ul.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`);
		if (!el) return;
		el.scrollIntoView({ block: "nearest" });
	}, [activeIndex, open]);

	const showPreview = Boolean(enablePreview && isMdUp && quickLookOpen);

	const emptyState = useMemo(() => {
		if (!query.trim()) return "Search across shift events";
		if (!canSearch) return `Enter ${MIN_LEN}+ characters`;
		if (isFetching && filteredItems.length === 0) return "Searching…";
		if (items.length > 0 && filteredItems.length === 0 && filters.size > 0)
			return "No shift events in selected filters";
		return "No results";
	}, [query, canSearch, isFetching, filteredItems.length, items.length, filters.size]);

	const openRow = (row: ShiftSearchRow) => {
		onOpenItem(row);
		onClose();
	};

	const openNewTab = (row: ShiftSearchRow) => {
		const url = buildItemUrl?.(row);
		if (!url) return;
		window.open(url, "_blank", "noopener,noreferrer");
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Escape") {
			e.preventDefault();
			onClose();
			return;
		}

		if (e.key === "ArrowDown") {
			e.preventDefault();
			setActiveIndex((i) =>
				Math.min(i + 1, Math.max(0, flat.length - 1)),
			);
			return;
		}

		if (e.key === "ArrowUp") {
			e.preventDefault();
			setActiveIndex((i) => Math.max(i - 1, 0));
			return;
		}

		// Shift+Enter toggles quick look (same behavior as Procedures palette)
		if (e.key === "Enter" && e.shiftKey) {
			e.preventDefault();
			if (enablePreview) setQuickLookOpen((v) => !v);
			return;
		}

		if (e.key === "Enter") {
			e.preventDefault();
			if (!activeItem) return;

			if ((e.ctrlKey || e.metaKey) && buildItemUrl) {
				openNewTab(activeItem);
				onClose();
				return;
			}
			openRow(activeItem);
		}
	};

	const chipBaseSx = (selected: boolean) =>
		searchPaletteNeutralChipSx(theme, selected);

	const filterChipSx = (filter: ShiftFilterKey, selected: boolean) => ({
		...searchPaletteAccentChipSx(
			theme,
			getShiftFilterAccent(theme, filter),
			selected,
		),
	});

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="lg"
			sx={{
				"& .MuiDialog-container": {
					alignItems: "flex-start",
					paddingTop: { xs: 1.5, sm: 3 },
				},
			}}
			PaperProps={{
				sx: (t) => searchPalettePaperSx(t),
			}}
		>
			{/* Header */}
			<Box
				sx={{ px: 2, pt: 1.5, pb: 1 }}
			>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					sx={{ mb: 1 }}
				>
					<Typography sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
						Global search
					</Typography>
				</Stack>

				<TextField
					fullWidth
					placeholder={placeholder}
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onKeyDown={onKeyDown}
					inputRef={inputRef}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
							</InputAdornment>
						),
						endAdornment: (
							<InputAdornment position="end">
								{isFetching ? (
									<CircularProgress size={16} sx={{ mr: 1 }} />
								) : null}
								{query ? (
									<IconButton
										size="small"
										aria-label="Clear"
										onClick={() => setQuery("")}
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								) : null}
							</InputAdornment>
						),
					}}
					sx={(t) => searchPaletteInputSx(t)}
				/>

				{canSearch ? (
					<Stack
						direction="row"
						alignItems="center"
						spacing={0.75}
						flexWrap="wrap"
						useFlexGap
						sx={{ mt: 1 }}
					>
						<Chip
							label="All"
							clickable
							variant="outlined"
							onClick={clearFilters}
							sx={chipBaseSx(filters.size === 0)}
						/>
						{SHIFT_FILTER_OPTIONS.map((option) => (
							<Chip
								key={`shift-filter-chip-${option.key}`}
								label={option.label}
								clickable
								variant="outlined"
								onClick={() => toggleFilter(option.key)}
								sx={filterChipSx(option.key, filters.has(option.key))}
							/>
						))}
						{items.length > 0 && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ ml: 0.5, fontWeight: 800 }}
							>
								{`${filteredItems.length} shown of ${items.length} matches`}
							</Typography>
						)}
					</Stack>
				) : null}
			</Box>

			<Divider />

			{/* Body */}
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: showPreview ? "420px 1fr" : "1fr",
					height: flat.length > 0 ? { xs: "52vh", sm: "46vh" } : "auto",
					minHeight: 0,
				}}
			>
				{/* Results */}
				<Box
					sx={{
						px: 1.25,
						py: filteredItems.length === 0 ? 0.75 : 1.25,
						overflow: "auto",
						minHeight: 0,
					}}
				>
					{flat.length === 0 ? (
						// no giant empty container — only show a small caption when user typed something OR needs guidance
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontWeight: 700, px: 0.75 }}
						>
							{emptyState}
						</Typography>
					) : (
						<List
							ref={listRef}
							disablePadding
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 0.75,
								minHeight: 0,
							}}
						>
							{groups.map((group) => (
								<Box key={group.filter}>
									<SearchPaletteSectionHeader
										label={group.label}
										count={group.items.length}
										accentColor={getShiftFilterAccent(theme, group.filter)}
									/>

									<Stack spacing={0.75}>
										{group.items.map(({ item: row, index: idx }) => {
											const selected = idx === activeIndex;
											const rowCategory = getShiftCategory(row);

											const descriptionText = stripHtml(getRowDescription(row));
											const title =
												descriptionText?.slice(0, 72) ||
												getRowLoggedBy(row) ||
												"Shift event";

											const dateText = formatShiftDateTime(
												getRowDateValue(row),
												timezone,
											);
											const loggedBy = getRowLoggedBy(row);
											const by = loggedBy ? `by ${loggedBy}` : "";

											const crew = getRowCrew(row);
											const crewName = pickString(crew?.Name, crew?.name);
											const groups = getRowGroups(row);
											const systems = getRowSystems(row);
											const groupName = groups.length
												? pickString(groups[0]?.Name, groups[0]?.name)
												: "";
											const systemName = systems.length
												? pickString(systems[0]?.Name, systems[0]?.name)
												: "";
											const typeLabel = getShiftFilterLabel(rowCategory);
											const secondaryMeta = [
												dateText ? `${dateText}${by ? ` · ${by}` : ""}` : by,
												crewName,
												groupName,
												systemName,
											].filter(Boolean);

											return (
												<ListItemButton
													key={String(row.Id ?? row.id ?? idx)}
													data-idx={idx}
													onMouseMove={() => setActiveIndex(idx)}
													onClick={() => {
														setActiveIndex(idx);
														openRow(row);
													}}
													sx={(t) => searchPaletteRowSx(t, selected)}
												>
													<Box
														sx={{
															width: 6,
															borderRadius: 2,
															bgcolor: getShiftFilterTint(
																theme,
																rowCategory,
																0.55,
															),
															flex: "0 0 auto",
														}}
													/>

													<Box sx={{ flex: 1, minWidth: 0 }}>
														<Typography
															sx={{
																fontWeight: 900,
																display: "-webkit-box",
																WebkitLineClamp: 2,
																WebkitBoxOrient: "vertical" as const,
																overflow: "hidden",
																lineHeight: 1.2,
															}}
															title={descriptionText || title}
														>
															{title}
														</Typography>

														<Typography
															variant="caption"
															color="text.secondary"
															sx={{ mt: 0.45, ...searchPaletteMetaTextSx }}
														>
															{secondaryMeta.join(" · ") || "No metadata"}
														</Typography>

														{descriptionText && (
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{ mt: 0.35, ...searchPaletteSummaryTextSx }}
																title={descriptionText}
															>
																{descriptionText}
															</Typography>
														)}
													</Box>

													<Stack
														direction="row"
														spacing={0.75}
														alignItems="flex-start"
													>
														<Chip
															size="small"
															label={typeLabel}
															variant="outlined"
															sx={{
																borderRadius: 2,
																fontWeight: 900,
																height: 24,
																bgcolor: getShiftFilterTint(
																	theme,
																	rowCategory,
																	0.08,
																),
																borderColor: getShiftFilterTint(
																	theme,
																	rowCategory,
																	0.24,
																),
																color: getShiftFilterAccent(theme, rowCategory),
															}}
														/>

														{buildItemUrl && isMdUp && (
															<IconButton
																className="hover-actions"
																size="small"
																aria-label="Open in new tab"
																onClick={(e) => {
																	e.preventDefault();
																	e.stopPropagation();
																	openNewTab(row);
																}}
																sx={(t) => ({
																	opacity: 0,
																	transition: "opacity 120ms ease",
																	mt: "-2px",
																	color: alpha(t.palette.text.primary, 0.55),
																	"&:hover": {
																		color: alpha(
																			t.palette.text.primary,
																			0.9,
																		),
																	},
																})}
															>
																<LaunchIcon fontSize="inherit" />
															</IconButton>
														)}
													</Stack>
												</ListItemButton>
											);
										})}
									</Stack>
								</Box>
							))}
						</List>
					)}
				</Box>

				{/* Preview */}
				{showPreview && (
					<SearchPaletteQuickLookPanel
						accentColor={
							activeItem
								? getShiftFilterAccent(theme, getShiftCategory(activeItem))
								: theme.palette.primary.main
						}
						label={
							activeItem
								? getShiftFilterLabel(getShiftCategory(activeItem))
								: "Shift event"
						}
						title={
							activeItem
								? stripHtml(getRowDescription(activeItem)).slice(0, 110) || "Event"
								: undefined
						}
						subtitle={
							activeItem
								? `${formatShiftDateTime(getRowDateValue(activeItem), timezone)}${
										getRowLoggedBy(activeItem)
											? ` · by ${getRowLoggedBy(activeItem)}`
											: ""
									}`
								: undefined
						}
						body={
							activeItem && getRowDescription(activeItem) ? (
								<Typography color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
									{stripHtml(getRowDescription(activeItem))}
								</Typography>
							) : undefined
						}
						meta={
							activeItem ? (
								<SearchPaletteAttributeList
									items={[
										{
											label: "Type",
											value: getShiftFilterLabel(getShiftCategory(activeItem)),
										},
										{
											label: "Time",
											value: formatShiftDateTime(
												getRowDateValue(activeItem),
												timezone,
											),
										},
										{ label: "Author", value: getRowLoggedBy(activeItem) },
										{
											label: "Crew",
											value: pickString(
												getRowCrew(activeItem)?.Name,
												getRowCrew(activeItem)?.name,
											),
										},
										{
											label: "Group",
											value:
												getRowGroups(activeItem).length > 0
													? pickString(
															getRowGroups(activeItem)[0]?.Name,
															getRowGroups(activeItem)[0]?.name,
														)
													: "",
										},
										{
											label: "System",
											value:
												getRowSystems(activeItem).length > 0
													? pickString(
															getRowSystems(activeItem)[0]?.Name,
															getRowSystems(activeItem)[0]?.name,
														)
													: "",
										},
										{
											label: "Tags",
											value: (activeItem.Tags ?? activeItem.tags ?? [])
												.slice(0, 3)
												.join(" · "),
										},
									]}
								/>
							) : undefined
						}
						bodyLabel="Summary"
						metaLabel="Attributes"
						actions={
							activeItem ? (
								<Stack direction="row" spacing={1}>
									<Button
										variant="contained"
										startIcon={<KeyboardReturnIcon />}
										onClick={() => openRow(activeItem)}
										sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
									>
										Open
									</Button>
									{buildItemUrl ? (
										<Button
											variant="outlined"
											startIcon={<LaunchIcon />}
											onClick={() => {
												openNewTab(activeItem);
												onClose();
											}}
											sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
										>
											New tab
										</Button>
									) : null}
								</Stack>
							) : undefined
						}
					/>
				)}
			</Box>

			{enablePreview && isMdUp ? (
				<>
					<Divider />
					<Box
						sx={(t) => ({
							px: 2,
							py: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-end",
							bgcolor: t.palette.background.paper,
						})}
					>
						<Button
							size="small"
							variant="contained"
							startIcon={<PreviewIcon fontSize="small" />}
							onClick={() => setQuickLookOpen((v) => !v)}
							sx={(t) => searchPaletteQuickLookToggleSx(t, quickLookOpen)}
						>
							Preview
						</Button>
					</Box>
				</>
			) : null}
		</Dialog>
	);
};
